import React, { useEffect, useState } from "react";

import { toast } from "../../../utils/notify";

import { Eye } from "lucide-react";

import ApiLoader from "../../ui/ApiLoader";

import { useAuth } from "../../../context/AuthContext";

import {
  fetchSensitiveData,
  fetchSensitiveDataById,
} from "../../../services/axios/AuthApis/DashboardApis";
import { DataTable } from "../../common/DataTable";

const SensitiveData = ({ activeTab }) => {
  const { user, isPlatformAdmin } = useAuth();

  const [sensitiveData, setSensitiveData] = useState([]);

  const [sensitiveLoading, setSensitiveLoading] = useState(false);

  const [selectedSensitiveData, setSelectedSensitiveData] = useState(null);

  const [showSensitiveModal, setShowSensitiveModal] = useState(false);

  const loadSensitiveData = async () => {
    try {
      setSensitiveLoading(true);

      // Reuses the Assets API with sensitiveData=true; the backend
      // maps/stores sensitive assets and returns the Sensitive Data table.
      const customerId = isPlatformAdmin
        ? ""
        : user?.customer_id || user?.id || user?._id || user?.customer?.id || "";

      const response = await fetchSensitiveData(customerId);

      const data = response.data || response || [];

      setSensitiveData(data);
    } catch (error) {
      console.log(error);

      toast.error("Failed to load sensitive data");
    } finally {
      setSensitiveLoading(false);
    }
  };

  const handleViewSensitive = async (id) => {
    try {
      const response = await fetchSensitiveDataById(id);

      setSelectedSensitiveData(response.data || response);

      setShowSensitiveModal(true);
    } catch (error) {
      console.log(error);

      toast.error("Unable to load details");
    }
  };

  useEffect(() => {
    if (activeTab === "sensitive") {
      loadSensitiveData();
    }
  }, [activeTab]);

  const uniqueValues = (key) => [...new Set(sensitiveData.map((item) => item[key]).filter(Boolean))];

  const columns = [
    {
      id: "data_type",
      accessorFn: (item) => item.data_type,
      header: "Data Type",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Data Type",
          placeholder: "All data types",
          options: uniqueValues("data_type").map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.data_type || "-"}</span>,
    },
    {
      id: "location",
      accessorFn: (item) => item.location,
      header: "Location",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Location", placeholder: "Search location" } },
      cell: ({ row }) => <span>{row.original.location || "-"}</span>,
    },
    {
      id: "classification",
      accessorFn: (item) => item.classification,
      header: "Classification",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Classification",
          placeholder: "All classifications",
          options: uniqueValues("classification").map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.classification || "-"}</span>,
    },
    {
      id: "cloud_provider",
      accessorFn: (item) => item.cloud_provider,
      header: "Cloud Provider",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Cloud Provider",
          placeholder: "All providers",
          options: uniqueValues("cloud_provider").map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span>{row.original.cloud_provider || "-"}</span>,
    },
    {
      id: "risk_level",
      accessorFn: (item) => item.risk_level,
      header: "Risk",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Risk",
          placeholder: "All risk levels",
          options: uniqueValues("risk_level").map((v) => ({ value: v, label: v })),
        },
      },
      cell: ({ row }) => <span className="font-semibold">{row.original.risk_level || "Low"}</span>,
    },
    {
      id: "status",
      accessorFn: (item) => item.status,
      header: "Status",
      enableSorting: false,
      meta: {
        filter: {
          variant: "select",
          label: "Status",
          placeholder: "All statuses",
          options: [
            { value: "exposed", label: "Exposed" },
            { value: "Protected", label: "Protected" },
          ],
        },
      },
      cell: ({ row }) => (
        <span
          className={`px-3 py-1 rounded-full text-sm ${
            row.original.status === "exposed" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
          }`}
        >
          {row.original.status || "Protected"}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Action",
      enableSorting: false,
      cell: ({ row }) => (
        <button
          onClick={() => handleViewSensitive(row.original._id || row.original.id)}
          className="p-2 border rounded-lg"
        >
          <Eye size={18} />
        </button>
      ),
    },
  ];

  if (sensitiveLoading && sensitiveData.length === 0) {
    return (
      <ApiLoader
        variant="spinner"
        label="Loading Sensitive Data..."
        className="py-20"
      />
    );
  }
  return (
    <>
      {activeTab === "sensitive" && (
        <>
          <h2 className="text-2xl font-bold mb-6">Data classification</h2>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-lg p-5">
              <p className="text-gray-500">Total Sensitive Records</p>

              <h2 className="text-3xl font-bold mt-3">
                {sensitiveData.length}
              </h2>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-5">
              <p className="text-gray-500">PII Data</p>

              <h2 className="text-3xl font-bold mt-3 text-blue-600">
                {
                  sensitiveData.filter((item) => item.data_type === "pii")
                    .length
                }
              </h2>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-5">
              <p className="text-gray-500">Financial Data</p>

              <h2 className="text-3xl font-bold mt-3 text-green-600">
                {
                  sensitiveData.filter((item) => item.data_type === "financial")
                    .length
                }
              </h2>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-5">
              <p className="text-gray-500">Secrets Found</p>

              <h2 className="text-3xl font-bold mt-3 text-red-600">
                {
                  sensitiveData.filter((item) => item.data_type === "secret")
                    .length
                }
              </h2>
            </div>
          </div>
          {/* Sensitive Data Table */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold">
                Data classification Inventory
              </h2>
            </div>

            <DataTable
              columns={columns}
              data={sensitiveData}
              getRowId={(item, index) => String(item._id || item.id || index)}
              loading={sensitiveLoading}
              emptyMessage="No Sensitive Data Found"
              enableColumnVisibility={false}
              enableColumnResizing
              enableColumnFilters
              maxBodyHeight="65vh"
            />
          </div>
          {/* Sensitive Data View Modal */}
          {showSensitiveModal && selectedSensitiveData && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl w-[600px] p-6">
                <div className="flex justify-between items-center mb-5">
                  <h2 className="text-xl font-bold">
                    Data classification Details
                  </h2>

                  <button
                    onClick={() => {
                      setShowSensitiveModal(false);

                      setSelectedSensitiveData(null);
                    }}
                    className="text-xl"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Description */}
                  <div>
                    <div className="border rounded-xl p-4 text-white bg-blue-600">
                      {selectedSensitiveData.description || "No description"}
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-2 gap-x-10 gap-y-5">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Data Type
                      </p>
                      <p className="font-medium">
                        {selectedSensitiveData.data_type || "-"}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Location
                      </p>
                      <p>{selectedSensitiveData.location || "-"}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Cloud Provider
                      </p>
                      <p>{selectedSensitiveData.cloud_provider || "-"}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">Service</p>
                      <p>{selectedSensitiveData.service || "-"}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Risk Level
                      </p>

                      <span className="px-3 py-1 rounded-full bg-red-100 text-red-600">
                        {selectedSensitiveData.risk_level || "Low"}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">Status</p>

                      <span
                        className={`px-3 py-1 rounded-full ${
                          selectedSensitiveData.status === "exposed"
                            ? "bg-red-100 text-red-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {selectedSensitiveData.status || "Protected"}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Detected Date
                      </p>
                      <p>{selectedSensitiveData.created_at || "-"}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => {
                      setShowSensitiveModal(false);

                      setSelectedSensitiveData(null);
                    }}
                    className="bg-blue-600 text-white px-5 py-2 rounded-lg"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default SensitiveData;
